import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import type { RequestStatus } from '@crm/shared'
import {
  getAssistantDashboard,
  type AssistantManagerCard,
  type AssistantRequestRow,
} from '../../shared/api/dashboard.api'
import { useAuthStore } from '../auth/authStore'
import { PageLoader } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { RequestStatusBadge } from '../../shared/ui/Badge'
import { NewsFeedWidget } from '../news/NewsFeedWidget'
import { AssistantManagerPlan } from './AssistantManagerPlan'

/**
 * Дашборд роли ASSISTANT (Помощник): что нужно проработать по заявкам/клиентам
 * прикреплённых менеджеров — выставить КП, дедлайны, разбивка по менеджерам.
 * С 29.09.2026 на карточке менеджера — его план и факт месяца.
 */

function formatDeadline(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

function ManagerCard({ m }: { m: AssistantManagerCard }) {
  // Каждый счётчик ведёт в список заявок, отфильтрованный по этому менеджеру и
  // тому же срезу, что и цифра на карточке. Наборы статусов («В работе» = 3,
  // «КП выставлено» = 2) и дедлайн-пресеты (due_soon/due_overdue) считаются на
  // бэке 1:1 с buildAssistantDashboard — число на странице совпадает с цифрой.
  const base = `/requests?managerId=${m.id}`
  const counters: Array<{ label: string; value: number; to: string; className?: string }> = [
    { label: 'Новые', value: m.newCount, to: `${base}&statuses=NEW` },
    {
      label: 'В работе',
      value: m.inProgressCount,
      to: `${base}&statuses=SENT_TO_PROCUREMENT,IN_PROGRESS,PARTIALLY_PROCESSED`,
    },
    {
      label: 'Готовы к КП',
      value: m.readyForQuotationCount,
      className: 'text-green-600',
      to: `${base}&statuses=QUOTATION_READY`,
    },
    {
      label: 'КП выставлено',
      value: m.quoteSentCount,
      to: `${base}&statuses=QUOTATION_CREATED,QUOTE_SENT`,
    },
    {
      label: 'Горят (3 дня)',
      value: m.burningCount,
      className: 'text-amber-600',
      to: `${base}&filter=due_soon`,
    },
    {
      label: 'Просрочено',
      value: m.overdueCount,
      className: 'text-red-600',
      to: `${base}&filter=due_overdue`,
    },
  ]
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="font-medium text-gray-900">{m.name || m.email}</div>
          <div className="text-xs text-gray-400">
            Клиентов: {m.clientsCount} · Активных заявок: {m.activeTotal}
          </div>
        </div>
        <Link to={base} className="text-xs text-brand-600 hover:text-brand-700 whitespace-nowrap">
          Заявки →
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {counters.map((c) => (
          <Link
            key={c.label}
            to={c.to}
            className="rounded-lg bg-gray-50 dark:bg-gray-800 px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 hover:ring-1 hover:ring-brand-300 transition-colors block"
            title={`${c.label}: открыть заявки ${m.name || m.email}`}
          >
            <div className={`text-lg font-semibold ${c.className ?? 'text-gray-800'}`}>
              {c.value}
            </div>
            <div className="text-[11px] text-gray-400 leading-tight">{c.label}</div>
          </Link>
        ))}
      </div>
      {m.plan && <AssistantManagerPlan plan={m.plan} />}
    </div>
  )
}

function AttentionTable({
  title,
  hint,
  rows,
  emptyText,
}: {
  title: string
  hint: string
  rows: AssistantRequestRow[]
  emptyText: string
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-2">
      <div>
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        <p className="text-xs text-gray-400">{hint}</p>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 py-3">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400">
                <th className="py-1.5 pr-3 font-medium">Заявка</th>
                <th className="py-1.5 pr-3 font-medium">Клиент</th>
                <th className="py-1.5 pr-3 font-medium">Менеджер</th>
                <th className="py-1.5 pr-3 font-medium">Статус</th>
                <th className="py-1.5 font-medium">Дедлайн</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="py-1.5 pr-3">
                    <Link to={`/requests/${r.id}`} className="text-brand-600 hover:underline">
                      {r.requestNumber || r.id.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="py-1.5 pr-3 text-gray-700">
                    {r.clientCode || r.clientName || '—'}
                  </td>
                  <td className="py-1.5 pr-3 text-gray-500">{r.managerName || '—'}</td>
                  <td className="py-1.5 pr-3">
                    <RequestStatusBadge status={r.status as RequestStatus} />
                  </td>
                  <td className="py-1.5 text-gray-700">{formatDeadline(r.deadline)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export function AssistantDashboard() {
  const { user } = useAuthStore()
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'assistant'],
    queryFn: getAssistantDashboard,
  })

  if (isLoading) return <PageLoader />
  if (error) return <ErrorMessage error={error} />
  if (!data) return null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Рабочий стол помощника{user?.name ? ` · ${user.name}` : ''}
          </h1>
          <p className="text-xs text-gray-400">
            Заявки и клиенты менеджеров, к которым вы прикреплены
          </p>
        </div>
        <Link
          to="/requests/new"
          className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors"
        >
          + Новая заявка
        </Link>
      </div>

      {data.managers.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500">
          Вы пока не прикреплены ни к одному менеджеру. Попросите администратора назначить
          менеджеров в разделе «Аккаунты».
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.managers.map((m) => (
            <ManagerCard key={m.id} m={m} />
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <AttentionTable
          title="Выставить КП"
          hint="Заявки в статусе «Готова к КП» — просчёт закупки завершён"
          rows={data.attention.needQuotation}
          emptyText="Нет заявок, ожидающих КП"
        />
        <AttentionTable
          title="Просроченные дедлайны"
          hint="Активные заявки с дедлайном в прошлом"
          rows={data.attention.overdue}
          emptyText="Просроченных заявок нет"
        />
      </div>

      <AttentionTable
        title="Горящие дедлайны"
        hint="Дедлайн в ближайшие 3 дня"
        rows={data.attention.upcoming}
        emptyText="Ближайших дедлайнов нет"
      />

      <NewsFeedWidget />
    </div>
  )
}
